'use client';

import React from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/components/providers/ThemeProvider';

export const DarkModeToggle: React.FC = () => {
  const { mode, toggleMode } = useTheme();
  const isDark = mode === 'dark';

  return (
    <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'} arrow>
      <IconButton
        onClick={toggleMode}
        sx={{
          width: 56,
          height: 32,
          borderRadius: '16px',
          bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
          border: '2px solid',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
          transition: 'all 250ms ease-in-out',
          overflow: 'hidden',
          position: 'relative',
          '&:hover': {
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)',
            transform: 'scale(1.05)',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Moving toggle indicator */}
          <motion.div
            style={{
              position: 'absolute',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: isDark
                ? 'linear-gradient(135deg, #a78bfa 0%, #635bff 100%)'
                : 'linear-gradient(135deg, #FEDF00 0%, #f59e0b 100%)',
              boxShadow: isDark
                ? '0 2px 8px rgba(167, 139, 250, 0.4)'
                : '0 2px 8px rgba(254, 223, 0, 0.4)',
            }}
            animate={{
              x: isDark ? 12 : -12,
            }}
            transition={{
              type: 'spring',
              stiffness: 700,
              damping: 30,
            }}
          />

          {/* Icons container */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 0.5,
              zIndex: 1,
            }}
          >
            {/* Sun icon */}
            <AnimatePresence mode="wait">
              {!isDark && (
                <motion.div
                  key="sun"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0, rotate: 180 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SunIcon />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Moon icon */}
            <AnimatePresence mode="wait">
              {isDark && (
                <motion.div
                  key="moon"
                  initial={{ scale: 0, rotate: 180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0, rotate: -180 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 'auto',
                  }}
                >
                  <MoonIcon />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Box>
      </IconButton>
    </Tooltip>
  );
};

// Sun icon component
const SunIcon = () => (
  <motion.svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    animate={{ rotate: 360 }}
    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
  >
    <circle cx="12" cy="12" r="4" fill="#0a2540" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
      <motion.line
        key={angle}
        x1="12"
        y1="2"
        x2="12"
        y2="6"
        stroke="#0a2540"
        strokeWidth="2"
        strokeLinecap="round"
        transform={`rotate(${angle} 12 12)`}
        animate={{
          opacity: [0.5, 1, 0.5],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          delay: angle / 360,
        }}
      />
    ))}
  </motion.svg>
);

// Moon icon component
const MoonIcon = () => (
  <motion.svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    initial={{ rotate: -30 }}
    animate={{ rotate: 0 }}
    transition={{ duration: 0.5 }}
  >
    <motion.path
      d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
      fill="#ffffff"
      animate={{
        opacity: [0.8, 1, 0.8],
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
    <motion.circle
      cx="17"
      cy="8"
      r="1.5"
      fill="rgba(255, 255, 255, 0.6)"
      animate={{
        scale: [1, 1.2, 1],
        opacity: [0.6, 0.9, 0.6],
      }}
      transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  </motion.svg>
);

// Alternative compact version
export const CompactDarkModeToggle: React.FC = () => {
  const { mode, toggleMode } = useTheme();
  const isDark = mode === 'dark';

  return (
    <Tooltip title={isDark ? 'Light mode' : 'Dark mode'} arrow>
      <IconButton
        onClick={toggleMode}
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
          transition: 'all 250ms ease-in-out',
          '&:hover': {
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
            transform: 'rotate(180deg) scale(1.1)',
          },
        }}
      >
        <AnimatePresence mode="wait">
          {isDark ? (
            <motion.div
              key="moon-compact"
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 90 }}
              transition={{ duration: 0.3, type: 'spring' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
                  fill="#a78bfa"
                />
              </svg>
            </motion.div>
          ) : (
            <motion.div
              key="sun-compact"
              initial={{ scale: 0, rotate: 90 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: -90 }}
              transition={{ duration: 0.3, type: 'spring' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="4" fill="#f59e0b" />
                {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                  <line
                    key={angle}
                    x1="12"
                    y1="2"
                    x2="12"
                    y2="6"
                    stroke="#f59e0b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    transform={`rotate(${angle} 12 12)`}
                  />
                ))}
              </svg>
            </motion.div>
          )}
        </AnimatePresence>
      </IconButton>
    </Tooltip>
  );
};
